import { BOTTOM_NAV_HEIGHT } from "@elyxeer/ui";
import Container from "@mui/material/Container";
import type { SxProps, Theme } from "@mui/material/styles";
import type { ReactNode } from "react";

/**
 * The standard page wrapper: one set of gutters, one max width, and the bottom padding
 * a phone needs to clear the tab bar and the home indicator.
 *
 * On desktop the tab bar is gone, so that reserved space is dropped and the gutters
 * open up. Every route renders inside one of these, which is what keeps the spacing
 * consistent without each screen re-deriving it. The surface underneath is painted by
 * `AppShell`, not here.
 *
 * The tabs share the default width on purpose. The column centres itself in whatever
 * the rail leaves, so its distance from the rail follows from its width: one tab on a
 * wider column than the others sits visibly closer to the rail, and moving between
 * tabs makes the whole page jump sideways.
 */
export function Screen({
  children,
  sx,
  maxWidth = "lg",
  disableTopInset = false,
  disableGutters = false,
}: {
  children: ReactNode;
  sx?: SxProps<Theme>;
  /** `lg` for the tabs and their settings, `md` for reading, `sm` for a single task. */
  maxWidth?: "xs" | "sm" | "md" | "lg" | "xl";
  /** Set when the route draws its own header, which has already cleared the notch. */
  disableTopInset?: boolean;
  /** Let a route own horizontal padding, for an edge-to-edge child such as a scroller. */
  disableGutters?: boolean;
}) {
  return (
    <Container
      maxWidth={maxWidth}
      disableGutters={disableGutters}
      sx={[
        {
          paddingInline: disableGutters ? 0 : { xs: 2.5, md: 5 },
          // There is no top bar, so unless the route draws its own header the page
          // itself has to clear the notch.
          paddingTop: {
            xs: disableTopInset ? 2 : "calc(16px + env(safe-area-inset-top))",
            md: 6,
          },
          // Phones: clear the fixed tab bar plus the iOS home indicator.
          // Desktop: neither exists, so only breathing room is left.
          paddingBottom: {
            xs: `calc(${BOTTOM_NAV_HEIGHT + 24}px + env(safe-area-inset-bottom))`,
            md: 8,
          },
          // The container centres itself; without this the content would hug the rail.
          marginInline: "auto",
        },
        ...(Array.isArray(sx) ? sx : [sx]),
      ]}
    >
      {children}
    </Container>
  );
}
